import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Actor,
  AddonFeatureManager,
  Agency,
  AgencyManager,
  AuthenticationManager,
  DefaultAgencyManager
} from 'core';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { useRouteMatch } from 'react-router';
import moment from 'moment';
import i18n from 'i18n';
import _ from 'lodash';
import { numberWithCommas } from 'utils/StringUtil';
import { DefaultReportManager, ReportManager } from 'core/report/ReportManager';
import { DefaultOrderManager, OrderManager } from 'core/order/OrderManager';
import { Order, State } from 'core/order/Order';
import { ReportType } from 'core/report/ReportData';
import { useCallAPI } from 'hooks/useCallAPI';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { AdvertiserRole } from 'core/companyMember/CompanyRole';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { Permission } from 'core/auth/Permission';
import { toast } from 'react-toastify';
import { getPercentValue } from 'helper/MetricsHelper';
import {
  DefaultStoredValueManager,
  StoredValueManager
} from 'core/storedValue/StoredValueManager';
import { isSystemAdmin } from 'helper/ActorHelper';
import { FinalReportType } from 'core/finalReport/FinalReport';

export type OrderDetailModelData = {
  l1ObjectList: any[];
  canSettle: boolean;
  settleTips: string;
  showDownloadFinalReport: boolean;
  fetchingFinalReport: boolean;
  loading: boolean;
  orderExternalTypes: SelectOptions[];
  order?: Order;
  viewData: any;
  showDetail: boolean;
  showDownloadRtbReportModal: boolean;
  showDownloadOutdoorReportModal: boolean;
  showDownloadFinalReportModal: boolean;
  showApproveButton: boolean;
  showRejectButton: boolean;
  canEditOrder: boolean;
  showSettleButton: boolean;
  showUnsettleButton: boolean;
  showGoVideoReportButton: boolean;
  remainingStoredValueError: string | undefined;
  toggleShowDetail: () => void;
  setShowDownloadRtbReportModal: (show: boolean) => void;
  setShowDownloadOutdoorReportModal: (show: boolean) => void;
  setShowDownloadFinalReportModal: (show: boolean) => void;
  settleOrder: () => Promise<void>;
  unsettleOrder: () => Promise<void>;
  rejectOrder: () => Promise<void>;
  approveOrder: () => Promise<void>;
  getReportLink: (type: ReportType) => string;
  refreshOrderDetail: () => Promise<void>;
  openFinalReport: (
    reportType: FinalReportType,
    queryParamsString?: string
  ) => Promise<void>;
};

const defaultL1ObjectManager = new DefaultL1ObjectManager();
const defaultReportManager = new DefaultReportManager();
const defaultOrderManager = new DefaultOrderManager();
const defaultStoredValueManager = new DefaultStoredValueManager();
const defaultAgencyManager = new DefaultAgencyManager();

export const useOrderDetailModel = (
  orderNumber: string,
  localMetaDataUpdater: (actor: Actor | null) => Promise<void>,
  authenticationManager: AuthenticationManager,
  addonFeatureManager: AddonFeatureManager,
  l1ObjectManager: L1ObjectManager = defaultL1ObjectManager,
  reportManager: ReportManager = defaultReportManager,
  orderManager: OrderManager = defaultOrderManager,
  storedValueManager: StoredValueManager = defaultStoredValueManager,
  agencyManager: AgencyManager = defaultAgencyManager
): OrderDetailModelData => {
  const match = useRouteMatch();
  const [showDownloadRtbReportModal, setShowDownloadRtbReportModal] =
    useState(false);
  const [showDownloadOutdoorReportModal, setShowDownloadOutdoorReportModal] =
    useState(false);
  const [showDownloadFinalReportModal, setShowDownloadFinalReportModal] =
    useState(false);
  const [showDetail, setShowDetail] = useState(true);
  const [orderExternalTypes, setOrderExternalTypes] = useState<SelectOptions[]>(
    []
  );
  const [viewData, setViewData] = useState<any>({});
  const [order, setOrder] = useState<Order | undefined>(undefined);
  const { loading, callAPIs, callAPIsBySequence } = useCallAPI();
  const [l1ObjectList, setL1ObjectList] = useState<any[]>([]);
  const [fetchingFinalReport, setFetchingFinalReport] = useState(false);
  const [remainingStoredValueError, setRemainingStoredValueError] = useState<
    string | undefined
  >(undefined);
  const [agencyAddonFeatures, setAgencyAddonFeatures] = useState<string[]>([]);

  const genOrderViewData = useCallback(
    (order: Order, orderExternalTypes: SelectOptions[]) => {
      const orderViewData = getOrderViewData(
        order,
        orderExternalTypes,
        addonFeatureManager,
        authenticationManager
      );
      setViewData(orderViewData);
    },
    [addonFeatureManager, authenticationManager]
  );

  const fetchOrderData = useCallback(
    async (matchIsExact) => {
      callAPIs(
        [
          orderManager.getOrderExternalTypes.bind(orderManager),
          orderManager.getOrder.bind(orderManager, orderNumber)
        ],
        (orderExternalTypes, newOrder) => {
          callAPIs(
            [
              !matchIsExact
                ? () => []
                : l1ObjectManager.getL1Objects.bind(
                    l1ObjectManager,
                    newOrder.id
                  )
            ],
            (l1ObjectList) => {
              setL1ObjectList(l1ObjectList);
            }
          );
          setOrderExternalTypes(orderExternalTypes);
          setOrder(newOrder);
          genOrderViewData(newOrder, orderExternalTypes);
        }
      );
    },
    [orderNumber, orderManager, callAPIs, l1ObjectManager, genOrderViewData]
  );

  useEffect(() => {
    !match.isExact && order === undefined && fetchOrderData(false);
  }, [order, fetchOrderData, match.isExact]);

  useEffect(() => {
    match.isExact && fetchOrderData(true);
  }, [fetchOrderData, match.isExact]);

  const fetchAgencyAddonFeatureData = useCallback(
    (order: Order) => {
      callAPIs(
        [agencyManager.fetchAgency.bind(agencyManager, order.agencyId)],
        (agency: Agency) => {
          const addonFeatures = addonFeatureManager.convertAgencyAddonFeatures(
            agency.detail.addonFeatures
          );
          setAgencyAddonFeatures(addonFeatures);
        }
      );
    },
    [agencyManager, addonFeatureManager, callAPIs]
  );

  useEffect(() => {
    !_.isNil(order) && fetchAgencyAddonFeatureData(order);
  }, [order, fetchAgencyAddonFeatureData]);

  const settleOrder = useCallback(async () => {
    if (!order) {
      return;
    }

    callAPIsBySequence(
      [
        orderManager.settleOrder.bind(orderManager, order.id),
        orderManager.getOrder.bind(orderManager, order.orderNumber),
        l1ObjectManager.getL1Objects.bind(l1ObjectManager, order.id)
      ],
      (updatedOrder, l1ObjectList) => {
        setOrder(updatedOrder);
        genOrderViewData(updatedOrder, orderExternalTypes);
        setL1ObjectList(l1ObjectList);
        toast.success(i18n.t('orderDetail.labels.settleSuccess'));
      }
    );
  }, [
    callAPIsBySequence,
    orderManager,
    order,
    l1ObjectManager,
    orderExternalTypes,
    genOrderViewData
  ]);

  const unsettleOrder = useCallback(async () => {
    if (!order) {
      return;
    }

    callAPIsBySequence(
      [
        orderManager.unsettleOrder.bind(orderManager, order.id),
        orderManager.getOrder.bind(orderManager, order.orderNumber)
      ],
      (updatedOrder) => {
        setOrder(updatedOrder);
        genOrderViewData(updatedOrder, orderExternalTypes);
        toast.success(i18n.t('orderDetail.labels.unsettleSuccess'));
      }
    );
  }, [
    order,
    callAPIsBySequence,
    orderManager,
    genOrderViewData,
    orderExternalTypes
  ]);

  const approveOrder = useCallback(async () => {
    if (!order) {
      return;
    }
    callAPIsBySequence(
      [
        orderManager.approveOrder.bind(orderManager, order.id),
        orderManager.getOrder.bind(orderManager, order.orderNumber),
        async () => {
          await localMetaDataUpdater(authenticationManager.actor);
        }
      ],
      (updatedOrder) => {
        setOrder(updatedOrder);
        genOrderViewData(updatedOrder, orderExternalTypes);
        toast.success(i18n.t('orderDetail.labels.approveSuccess'));
      }
    );
  }, [
    callAPIsBySequence,
    orderManager,
    order,
    orderExternalTypes,
    genOrderViewData,
    localMetaDataUpdater,
    authenticationManager.actor
  ]);

  const rejectOrder = useCallback(async () => {
    if (!order) {
      return;
    }
    callAPIsBySequence(
      [
        orderManager.rejectOrder.bind(orderManager, order.id),
        orderManager.getOrder.bind(orderManager, order.orderNumber)
      ],
      (updatedOrder) => {
        setOrder(updatedOrder);
        genOrderViewData(updatedOrder, orderExternalTypes);
        toast.success(i18n.t('orderDetail.labels.rejectSuccess'));
      }
    );
  }, [
    callAPIsBySequence,
    orderManager,
    order,
    orderExternalTypes,
    genOrderViewData
  ]);

  const checkRemainingStoredValue = useCallback(
    async (order) => {
      const error = await validateBudgetWithRemainingStoredValue(
        order,
        addonFeatureManager,
        storedValueManager
      );
      error && setRemainingStoredValueError(error);
    },
    [addonFeatureManager, storedValueManager]
  );

  useEffect(() => {
    checkRemainingStoredValue(order);
  }, [order, checkRemainingStoredValue]);

  const toggleShowDetail = useCallback(() => {
    setShowDetail((value) => !value);
  }, []);

  const hasAliveOrNotStartCampaign = false;

  const showDownloadFinalReport = useMemo(() => {
    const hasAddonFeature = agencyAddonFeatures.includes(
      ADDONFEATURE.REPORT.REPORT_RESTRICT_FINALREPORT.toString()
    );
    if (_.isEmpty(agencyAddonFeatures)) {
      return false;
    }
    if (hasAddonFeature && !isSystemAdmin(authenticationManager.actor)) {
      return false;
    }
    if (!order || !order.projectName) {
      return false;
    }
    const canDownloadFinalReportDate = moment(order.startDate).startOf('day');
    if (moment().isAfter(canDownloadFinalReportDate)) {
      return true;
    }
    return false;
  }, [order, agencyAddonFeatures, authenticationManager.actor]);

  return {
    order,
    viewData: _.omitBy(viewData, _.isUndefined),
    loading,
    showDetail,
    orderExternalTypes,
    showDownloadRtbReportModal,
    showDownloadOutdoorReportModal,
    showGoVideoReportButton: showGoVideoReportButton(
      order,
      addonFeatureManager
    ),
    showApproveButton: showApproveButton(order),
    showRejectButton: showRejectButton(order),
    canEditOrder: canEditOrder(authenticationManager.actor, order),
    showSettleButton: showSettleButton(order, addonFeatureManager),
    showUnsettleButton: showUnsettleButton(order, addonFeatureManager),
    l1ObjectList,
    canSettle: getCanSettle(order, hasAliveOrNotStartCampaign),
    settleTips: getSettleTips(order, hasAliveOrNotStartCampaign),
    showDownloadFinalReport: showDownloadFinalReport,
    fetchingFinalReport,
    remainingStoredValueError,
    settleOrder,
    unsettleOrder,
    rejectOrder,
    approveOrder,
    getReportLink: _.partial(getReportLink, order),
    toggleShowDetail,
    refreshOrderDetail: () => fetchOrderData(true),
    setShowDownloadRtbReportModal,
    setShowDownloadOutdoorReportModal,
    showDownloadFinalReportModal,
    setShowDownloadFinalReportModal,
    openFinalReport: async (
      reportType: FinalReportType,
      queryParamsString = ''
    ) => {
      if (!order) {
        return;
      }
      const newTab = window.open(`/final-report?preparing`, '_blank');
      if (newTab) {
        newTab.opener = null;
      }
      setFetchingFinalReport(true);
      try {
        const reportFileId = await reportManager.uploadFinalReportData(
          order.id,
          reportType
        );
        const reportId =
          _.get(reportFileId, 'status', '') === 'INITIAL'
            ? _.get(reportFileId, 'payload', '')
            : '';
        if (newTab) {
          newTab.location.href =
            `/final-report?id=${reportId}&reportType=${reportType}` +
            queryParamsString;
        }
      } catch (e) {}
      setFetchingFinalReport(false);
    }
  };
};

const getOrderStateDes = (state) => {
  if (State.CHANGE_PENDING === state || State.NOT_APPROVE === state) {
    return i18n.t('orderDetail.labels.waitingForReview');
  }
  if (State.REJECT === state) {
    return i18n.t('orderDetail.labels.reject');
  }
  if (State.SETTLE === state) {
    return i18n.t('orderDetail.labels.settle');
  }
  if (State.SETTLED === state) {
    return i18n.t('orderDetail.labels.settled');
  }
  return i18n.t('orderDetail.labels.approve');
};

const getRemainDayDesc = (startDate, endDate) => {
  const now = moment();
  const today = now.startOf('day');
  if (moment(startDate).isAfter(today)) {
    return i18n.t('orderDetail.labels.notstart');
  }

  if (moment(endDate).add(1, 'day').startOf('day').isBefore(now)) {
    return i18n.t('orderDetail.labels.finished');
  }

  if (moment(endDate).startOf('day').isSame(today)) {
    return i18n.t('orderDetail.labels.today');
  }

  return i18n.t('orderDetail.labels.days', {
    days: moment(endDate).diff(today, 'days') + 1
  });
};

const getOrderViewData = (
  order: Order,
  orderExternalTypes: SelectOptions[],
  addonFeatureManager: AddonFeatureManager,
  authenticationManager: AuthenticationManager
) => {
  let totalBudget: any = formatPriceWithCurrency(order.currency, order.budget);
  let remainBudget: any = formatPriceWithCurrency(
    order.currency,
    order.budgetBalance
  );
  const orderMargin = _.round(order.orderMargin * 100, 2);
  let agencyProfit: any = `${orderMargin} %`;
  let modifyReason: any = undefined;
  if (order.state === State.CHANGE_PENDING) {
    if (order.changeBudget) {
      totalBudget = {
        origin: i18n.t('orderDetail.labels.originBudget', {
          budget: formatPriceWithCurrency(order.currency, order.budget)
        }),
        change: i18n.t('orderDetail.labels.changeBudget', {
          budget: formatPriceWithCurrency(order.currency, order.changeBudget)
        })
      };
      remainBudget = {
        origin: i18n.t('orderDetail.labels.originBudget', {
          budget: formatPriceWithCurrency(order.currency, order.budgetBalance)
        }),
        change: i18n.t('orderDetail.labels.changeBudget', {
          budget: formatPriceWithCurrency(
            order.currency,
            order.budgetBalance + order.changeBudget - order.budget
          )
        })
      };
    }
    if (order.changeOrderMargin) {
      agencyProfit = {
        origin: i18n.t('orderDetail.labels.originProfit', {
          profit: orderMargin
        }),
        change: i18n.t('orderDetail.labels.changeProfit', {
          profit: _.round(order.changeOrderMargin * 100, 2)
        })
      };
    }
    modifyReason = order.modifyReason;
  }
  const currentActorType = _.get(authenticationManager.actor, 'actorType');
  const canCheckAgencyProfit =
    addonFeatureManager.isFeatureEnable(
      ADDONFEATURE.CAMPAIGN.ORDER_AGENCY_PROFIT_SETTING
    ) &&
    ![
      AdvertiserRole.ROLE_ADV_ADMIN.toString(),
      AdvertiserRole.ROLE_ADV_REPORT.toString(),
      AdvertiserRole.ROLE_ADV_SALES.toString()
    ].includes(`ROLE_${currentActorType}`);
  const canCheckChannelMargin = isSystemAdmin(authenticationManager.actor);
  const orderExternalType = orderExternalTypes.find(
    (typeOption) => typeOption.value === order.externalType
  );
  const performance =
    order.externalType && ['Video', 'pDOOH_7-11'].includes(order.externalType)
      ? {
          impres: numberWithCommas(order.impres),
          adView: numberWithCommas(order.adView),
          viewRate: getPercentValue(order.adView, order.impres)
        }
      : {
          impres: numberWithCommas(order.impres),
          clicks: numberWithCommas(order.clicks),
          ctr: getPercentValue(order.clicks, order.impres)
        };

  const channelMarginData = Object.keys(order.channelMargin).reduce(
    (all, channelId) => {
      const channelName = i18n.t(`salesChannels.${channelId}`);
      all[
        i18n.t('orderDetail.labels.channelMargin', {
          channel: channelName
        })
      ] = canCheckChannelMargin
        ? `${_.round(order.channelMargin[channelId] * 100, 2)} %`
        : undefined;
      return all;
    },
    {}
  );

  const orderViewData: any = {
    id: order.id,
    name: order.projectName,
    startDate: order.startDate,
    endDate: order.endDate,
    basic: _.omitBy(
      {
        orderNumber: order.orderNumber,
        orderType: i18n.t(`orderType.enums.${order.orderType.toLowerCase()}`),
        externalId: _.defaultTo(
          order.externalId,
          i18n.t('common.labels.noData')
        ),
        externalType: _.get(
          orderExternalType,
          'label',
          i18n.t('common.labels.noData')
        ),
        dateRange: `${order.startDate} ~ ${order.endDate}`,
        remainDays: getRemainDayDesc(order.startDate, order.endDate),
        totalBudget: totalBudget,
        alreadySpent: formatPriceWithCurrency(order.currency, order.spent),
        remainBudget: remainBudget,
        progress: getPercentValue(order.spent, order.budget),
        agencyProfit: canCheckAgencyProfit ? agencyProfit : undefined,
        ...channelMarginData,
        status: getOrderStateDes(order.state),
        modifyReason,
        creator: order.creator,
        creatorEmail: order.creatorEmail,
        monitor: order.monitor
          ? i18n.t('common.labels.open')
          : i18n.t('common.labels.close')
      },
      _.isUndefined
    ),
    performance
  };
  return orderViewData;
};

const getSettleTips = (
  order: Order | undefined,
  hasAliveOrNotStartCampaign: boolean
): string => {
  if (!order) {
    return '';
  }
  if (order.state === State.SETTLE) {
    return i18n.t('orderDetail.labels.settleWait');
  }

  if (order.state === State.SETTLED) {
    return i18n.t('orderDetail.labels.settledNoNeedSettle');
  }

  if (hasAliveOrNotStartCampaign) {
    return i18n.t('orderDetail.labels.hasRunningCampaignCannotSettle');
  }

  return '';
};

const getCanSettle = (
  order: Order | undefined,
  hasAliveOrNotStartCampaign: boolean
): boolean => {
  if (!order) {
    return false;
  }
  if (order.state === State.SETTLED) {
    return false;
  }

  if (order.state === State.SETTLE) {
    return false;
  }
  return !hasAliveOrNotStartCampaign;
};

const getReportLink = (order: Order | undefined, type: ReportType): string => {
  if (!order) {
    return '/reports/performance';
  }
  const from = encodeURIComponent(
    moment(order.startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')
  );
  const to = encodeURIComponent(
    moment(order.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
  );
  return `/reports/performance?type=${type}&dimension=adsOrderId&from=${from}&to=${to}&adsOrderId=${order.id}`;
};

const showGoVideoReportButton = (
  order: Order | undefined,
  addonFeatureManager
): boolean => {
  if (!order) {
    return false;
  }

  return addonFeatureManager.isAnyVideoRelatedCreativeTypeEnable();
};

const showApproveButton = (order?: Order): boolean => {
  if (!order) {
    return false;
  }

  return (
    order.state === State.NOT_APPROVE || order.state === State.CHANGE_PENDING
  );
};

const showRejectButton = (order?: Order): boolean => {
  if (!order) {
    return false;
  }

  return (
    order.state === State.NOT_APPROVE || order.state === State.CHANGE_PENDING
  );
};

const canEditOrder = (actor: Actor | null, order?: Order): boolean => {
  if (!order || !actor) {
    return false;
  }

  return (
    ![State.SETTLE, State.SETTLED].includes(order.state) &&
    actor.permissions.indexOf(Permission.ORDER_WRITE) > -1 &&
    order.id >= 2000000
  );
};

const showSettleButton = (order, addonFeatureManager): boolean => {
  const permissionAllowOrderSettlement = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.COMPANY.AGENCY_ALLOW_ORDER_SETTLEMENT
  );
  const stateAllowSettlement =
    !!order &&
    order.state !== State.CHANGE_PENDING &&
    order.state !== State.NOT_APPROVE &&
    order.state !== State.REJECT;
  return permissionAllowOrderSettlement && stateAllowSettlement;
};

const showUnsettleButton = (order, addonFeatureManager): boolean => {
  const permissionAllowOrderSettlement = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.COMPANY.AGENCY_ALLOW_ORDER_SETTLEMENT
  );
  const stateAllowSettlement = !!order && order.state === State.SETTLE;
  return permissionAllowOrderSettlement && stateAllowSettlement;
};

const validateBudgetWithRemainingStoredValue = async (
  order: Order | undefined,
  addonFeatureManager: AddonFeatureManager,
  storedValueManager: StoredValueManager
): Promise<string | undefined> => {
  if (!order) {
    return;
  }
  if (
    addonFeatureManager.isFeatureEnable(ADDONFEATURE.COMPANY.STORED_VALUE) &&
    showApproveButton(order)
  ) {
    const remainingStoredValue =
      await storedValueManager.getRemainingStoredValue(order.agencyId);
    const budgetToCompare = !_.isNil(order.changeBudget)
      ? order.changeBudget - order.budget
      : order.budget;
    if (
      !_.isNil(remainingStoredValue) &&
      budgetToCompare > remainingStoredValue
    ) {
      return i18n.t('orderDetail.errors.budgetExceedStoredValueError', {
        amount: formatPriceWithCurrency(order.currency, remainingStoredValue)
      });
    }
  }
};
